import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { find, findIndex, propEq } from 'ramda';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import * as TrackCard from '@components/LibraryTrackCard';
import * as CourseCard from '@components/LibraryCourseCard';
import * as ModuleCard from '@components/LibraryModuleCard';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';

const TrainingEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    // URL Params
    const params = useParams();
    const id = Number(params.id);
    const type = params.activeTab;

    // Redux State Variables
    const templates = useSelector((state) => state.develop.templates);
    const companyId = useSelector((state) => state.app.company_info.id);

    // Local consts
    const template = location.state?.template 
        || find(propEq('id', id), templates[type]?.data);

    const assessmentId = template?.data?.assessment_id;

    // Local State Variables
    const [message, setMessage] = useState('Loading....');

    // Initial Load useEffect to fetch template if not already in state
    useEffect(() => {
        if (!template) {
            dispatch(DevelopActions.getlibrarytemplatessearchRequest(type, { id: id, published: [0,1] }));

            setTimeout(() => {
                setMessage(`Template not found.`);
            }, 3000);
        }
    }, []);

    // Fetch templates if not available via navigation state
    useEffect(() => {
        if (!template) {
            dispatch(DevelopActions.getlibrarytemplatessearchRequest(type, { id: id }));
        }
    }, [template]);

    useEffect(() => {
        if (assessmentId) {
            dispatch(DevelopActions.getassessmentsRequest(assessmentId));
        }
    }, [assessmentId]);


    // Event Handlers
    const handleCardMenu = (event) => {
        switch (event) {
            case 'detail view':
                navigate(`/library/training/${type}/${id}/view`);
                break;
            case 'edit':
                break;

            case 'preview view':
                if (type === 'tracks') {
                    toast.info('Coming Soon.', { position: toast.POSITION.TOP_RIGHT });
                } else if (type === 'courses') {
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Preview',
                            data: { before: { template, module: template.children[0], index: 0 } },
                        })
                    );
                } else if (type === 'modules') {
                    if (template.data.presentation_url && !template.data.presentation_images) {
                        toast.warn('This file is being converted to a presentation.  This process takes a few short minutes.', { position: toast.POSITION.TOP_RIGHT });
                        return;
                    }
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Preview',
                            data: { before: { course: { children: [template] }, module: template, index: 0 } },
                        })
                    );
                }
                break;

            default:
                break;
        }
    };

    const handleChildMenu = (event, item) => {
        switch (event) {
            case 'assign':
                if (type === 'track') {
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Assign Training',
                            data: { before: { disabled: ['tracks'], modules: [item], companyId } },
                        })
                    );
                }
                break;

            case 'detail view':
                handleShowDetail(item, 'view');
                break;

            case 'edit':
                handleShowDetail(item, 'edit');
                break;

            case 'preview view':
                if (type === 'tracks') {
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Preview',
                            data: { before: { course: item, module: item.children[0], index: 0 } },
                        })
                    );
                }
                if (type === 'courses') {
                    const index = findIndex(propEq('id', item.id), template.children);
                    if (index > -1) {
                        dispatch(
                            AppActions.modalRequest({
                                type: 'Preview',
                                data: { before: { template, module: { ...item, status: 0 }, index } },
                            })
                        );
                    }
                }
                break;

            case 'quick assign':
                if (type === 'track') {
                    dispatch(
                        AppActions.modalRequest({
                            type: 'Quick Edit',
                            data: {
                                before: {
                                    template: item,
                                    type: 'courses',
                                    from: 'template',
                                    deletable: false,
                                    companyId,
                                },
                            },
                            callBack: {
                                onDelete: () => dispatch(DevelopActions.libraryeventRequest('template', 'delete', item.id)),
                            },
                        })
                    );
                }
                break;
            default:
                break;
        }
    };

    const handleShowDetail = (card, mode = 'view') => {
        const target = type === 'tracks' ? 'courses' : 'modules';
        navigate(`/library/training/${target}/${card.id}/${mode}`,);
    };

    const EditComponent = {
        tracks: TrackCard.Edit,
        courses: CourseCard.Edit,
        modules: ModuleCard.Edit
    }[type];

    return (
        <ErrorBoundary>
            { !template  
                ? (
                     <div>{message}</div>
                ) : (
                    <EditComponent data={template} onCardMenu={handleCardMenu} onChildMenu={handleChildMenu}/>
                )
            }
        </ErrorBoundary>
    );
};

export default TrainingEdit;
